import { useState, useCallback, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Upload, Check, File, Folder, Archive, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UploadPage = () => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch existing package on mount
  useEffect(() => {
    fetch(`${API_URL}/api/upload/package`)
      .then(r => r.json())
      .then(data => {
        if (data.files && data.files.length > 0) setResult({ files: data.files, total_files: data.total });
      })
      .catch(() => {});
  }, []);

  const handleUpload = async (file) => {
    if (!file || !file.name.endsWith('.zip')) {
      setError('Please upload a .zip file');
      return;
    }
    setUploading(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/upload/package`, { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Upload failed'); return; }
      setResult(data);
    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleUpload(file);
  }, []);

  const getIcon = (path) => {
    if (path.endsWith('/')) return <Folder className="w-4 h-4 text-blue-400" />;
    const ext = path.split('.').pop().toLowerCase();
    if (['css', 'jsx', 'js', 'html', 'md'].includes(ext)) return <File className="w-4 h-4 text-purple-400" />;
    if (['png', 'jpg', 'jpeg', 'svg', 'webp', 'gif'].includes(ext)) return <File className="w-4 h-4 text-green-400" />;
    if (['mp4', 'webm', 'mov'].includes(ext)) return <File className="w-4 h-4 text-orange-400" />;
    return <File className="w-4 h-4 text-gray-400" />;
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div style={{ background: '#0a0a0a', minHeight: '100vh', padding: '60px 20px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <h1 style={{ fontSize: 32, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Package Upload</h1>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,.5)', marginBottom: 32 }}>Upload your website package as a .zip file. It will be extracted and available for processing.</p>
        </motion.div>

        {/* Drop zone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? '#7c6ef0' : 'rgba(255,255,255,.15)'}`,
            borderRadius: 16,
            padding: '48px 32px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragOver ? 'rgba(124,110,240,.05)' : 'rgba(255,255,255,.02)',
            transition: 'all 0.2s ease',
          }}
          data-testid="zip-drop-zone"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            style={{ display: 'none' }}
            onChange={(e) => handleUpload(e.target.files[0])}
            data-testid="zip-file-input"
          />
          {uploading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <Loader2 className="w-10 h-10 text-purple-400 animate-spin" />
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,.7)' }}>Uploading & extracting...</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <Archive className="w-10 h-10" style={{ color: 'rgba(255,255,255,.3)' }} />
              <p style={{ fontSize: 16, fontWeight: 500, color: 'rgba(255,255,255,.7)' }}>Drop your .zip here or click to browse</p>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,.35)' }}>Accepts: ariaone-website.zip (or any .zip package)</p>
            </div>
          )}
        </motion.div>

        {/* Error */}
        {error && (
          <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 8, background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)' }}>
            <p style={{ fontSize: 14, color: '#ef4444' }}>{error}</p>
          </div>
        )}

        {/* Result */}
        {result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            style={{ marginTop: 24, borderRadius: 12, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.08)', overflow: 'hidden' }}
            data-testid="package-result"
          >
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Check className="w-5 h-5 text-green-400" />
                <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>Package extracted</span>
              </div>
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,.4)' }}>{result.total_files} files</span>
            </div>
            <div style={{ maxHeight: 400, overflowY: 'auto', padding: '8px 0' }}>
              {(result.files || []).map((f, i) => {
                const path = typeof f === 'string' ? f : f.path;
                const size = typeof f === 'object' ? f.size : null;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 20px', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                      {getIcon(path)}
                      <span style={{ fontSize: 13, color: 'rgba(255,255,255,.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{path}</span>
                    </div>
                    {size && <span style={{ fontSize: 11, color: 'rgba(255,255,255,.3)', flexShrink: 0 }}>{formatSize(size)}</span>}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default UploadPage;
